/**
 * NmbrsSyncTrigger Component
 *
 * "Sync nu" button with confirmation dialog, progress spinner, and result summary.
 * Story Nmbrs-2.4: Handmatige Sync Trigger
 *
 * Design system: Navy #2F5A7A, Teal #54949E, Green #B7D945, Red #D64545
 */

import { useState, useEffect, useRef } from 'react';
import { useTriggerSync, useSyncLogDetail } from '../hooks/useNmbrsSync';
import { useQueryClient } from '@tanstack/react-query';

export function NmbrsSyncTrigger() {
  const triggerSync = useTriggerSync();
  const queryClient = useQueryClient();

  const [showConfirm, setShowConfirm] = useState(false);
  const [activeSyncLogId, setActiveSyncLogId] = useState<number | null>(null);
  const hasRefreshed = useRef(false);

  // Poll sync log detail while sync is running via React Query refetchInterval
  const { data: syncLogDetail } = useSyncLogDetail(activeSyncLogId ?? 0, {
    refetchInterval: activeSyncLogId ? 3000 : false,
  });

  const syncStatus = syncLogDetail?.data?.status;
  const isRunning = syncStatus === 'running';
  const isComplete = syncStatus === 'completed' || syncStatus === 'failed' || syncStatus === 'partial';

  // Refresh parent queries once when sync completes
  useEffect(() => {
    if (isComplete && activeSyncLogId && !hasRefreshed.current) {
      hasRefreshed.current = true;
      queryClient.invalidateQueries({ queryKey: ['nmbrs', 'sync', 'status'] });
      queryClient.invalidateQueries({ queryKey: ['nmbrs', 'sync', 'log'] });
    }
  }, [isComplete, activeSyncLogId, queryClient]);

  const handleTrigger = async () => {
    setShowConfirm(false);
    try {
      const result = await triggerSync.mutateAsync();
      const syncLogId = result.data.syncLogId;
      hasRefreshed.current = false;
      setActiveSyncLogId(syncLogId);
    } catch {
      // Error is shown via mutation state
    }
  };

  const handleDismiss = () => {
    setActiveSyncLogId(null);
  };

  // Show result summary after completion
  if (isComplete && syncLogDetail?.data) {
    const detail = syncLogDetail.data;
    const isSuccess = detail.status === 'completed';
    const isPartial = detail.status === 'partial';

    return (
      <div className="flex items-center gap-2">
        <div
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
            isSuccess
              ? 'bg-[#B7D945]/10 text-[#6B8C1A]'
              : isPartial
              ? 'bg-amber-50 text-amber-800'
              : 'bg-red-50 text-[#D64545]'
          }`}
        >
          <span>
            {detail.syncedCount}/{detail.totalEmployees} gesynchroniseerd
            {detail.errorCount > 0 && ` (${detail.errorCount} fout${detail.errorCount !== 1 ? 'en' : ''})`}
          </span>
          <button
            onClick={handleDismiss}
            className="ml-1 text-current opacity-50 hover:opacity-100"
            title="Sluiten"
            aria-label="Sluiten"
          >
            &times;
          </button>
        </div>
      </div>
    );
  }

  // Show spinner while running
  if (isRunning || triggerSync.isPending) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 text-sm text-[#2F5A7A]">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#2F5A7A]" />
        <span>Synchroniseren...</span>
      </div>
    );
  }

  // Show error if trigger failed
  if (triggerSync.isError) {
    const errorMsg = triggerSync.error instanceof Error ? triggerSync.error.message : 'Sync starten mislukt';
    const isConflict = errorMsg.includes('al een synchronisatie');

    return (
      <div className="flex items-center gap-2">
        <span className={`text-sm ${isConflict ? 'text-amber-600' : 'text-[#D64545]'}`}>
          {isConflict ? 'Er loopt al een sync' : 'Sync mislukt'}
        </span>
        <button
          onClick={() => triggerSync.reset()}
          className="px-3 py-1.5 text-sm font-medium rounded-lg border border-[#E2E8F0] text-[#2F5A7A] hover:bg-gray-50"
        >
          Opnieuw
        </button>
      </div>
    );
  }

  // Show confirmation dialog or button
  if (showConfirm) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-600">Alle medewerkers synchroniseren?</span>
        <button
          onClick={handleTrigger}
          className="px-3 py-1.5 text-sm font-medium rounded-lg bg-[#2F5A7A] text-white hover:bg-[#244A66]"
        >
          Ja, start sync
        </button>
        <button
          onClick={() => setShowConfirm(false)}
          className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
        >
          Annuleren
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setShowConfirm(true)}
      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-[#E2E8F0] text-[#2F5A7A] hover:bg-gray-50"
      title="Handmatige synchronisatie starten"
    >
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
      Sync nu
    </button>
  );
}
